import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ScanLine,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";
import { SeoHead } from "../../../seo/SeoHead";
import {
  buildBreadcrumbListJsonLd,
  buildFAQPageJsonLd,
} from "../../../seo/site";

const SEO_DESCRIPTION =
  "OCR Raguia : transformez vos PDF scannés, images et documents papier en données interrogeables par l'IA. Compatible tous formats, hébergé en France, RGPD.";

const FAQ = [
  {
    q: "Quels formats l'OCR supporte-t-il ?",
    a: "PDF natifs et scannés, JPG, PNG, TIFF, BMP, et images intégrées dans Word ou PowerPoint. Notre pipeline extrait le texte de tableaux, formulaires et documents complexes.",
  },
  {
    q: "L'OCR fonctionne-t-il sur des documents de mauvaise qualité ?",
    a: "Oui. Notre OCR intègre des algorithmes de correction d'image (débruitage, redressement) avant extraction. La reconnaissance reste élevée même sur des documents scannés à basse résolution.",
  },
  {
    q: "Les données extraites restent-elles en France ?",
    a: "Absolument. Tout le traitement OCR s'effectue sur nos serveurs OVH en France. Aucune donnée n'est envoyée vers des services hors Union Européenne.",
  },
  {
    q: "L'OCR peut-il lire des tableaux et données structurées ?",
    a: "Oui. Notre pipeline est optimisé pour les tableaux, formulaires et données structurées. L'IA peut répondre sur les valeurs de ces tableaux : bilans, grilles tarifaires, plannings...",
  },
];

export default function OcrPage() {
  const [open, setOpen] = useState(null);

  return (
    <>
      <SeoHead
        title="OCR IA pour documents — Raguia | PDF scannés, images, tableaux"
        description={SEO_DESCRIPTION}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Fonctionnalités", path: "/fonctionnalites" },
            { name: "OCR", path: "/fonctionnalites/ocr" },
          ]),
          buildFAQPageJsonLd(FAQ),
        ]}
      />
      <main className="min-h-screen bg-stone-50">
        <section className="relative px-4 pt-16 pb-12 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
          <div className="absolute top-20 right-10 size-96 bg-[#A43032]/8 rounded-full blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-8">
              <ScanLine className="size-4" /> Fonctionnalité OCR
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#010101] leading-[1.1] mb-6">
              Vos documents papier{" "}
              <span className="text-[#A43032]">deviennent interrogeables</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-2xl font-medium mb-10">
              Notre OCR avancé transforme vos PDF scannés, images et archives
              papier en base de connaissances que votre IA peut lire et citer.
              Aucun document ne vous échappe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-xl hover:scale-105 hover:bg-[#8a2729] transition-all"
              >
                Tester sur mes documents <ArrowRight className="size-5" />
              </Link>
              <Link
                to="/fonctionnalites"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-stone-200 bg-white px-8 py-4 text-base font-bold text-stone-700 hover:border-[#A43032] hover:text-[#A43032] transition-all"
              >
                Toutes les fonctionnalités
              </Link>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-2xl p-8 border-2 border-stone-300">
              <h2 className="font-heading text-xl font-semibold text-red-700 mb-6">
                Sans OCR : des documents inaccessibles
              </h2>
              <ul className="space-y-4 text-stone-600">
                {[
                  "Vos archives numérisées ne sont que des images.",
                  "Les PDF scannés ne peuvent pas être fouillés.",
                  "Des années de documents inaccessibles pour l'IA.",
                  "Retranscription manuelle longue et coûteuse.",
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-red-500 font-bold text-xl shrink-0">
                      ✗
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#010101] to-stone-900 rounded-2xl p-8 text-white border border-stone-800 shadow-xl">
              <h2 className="font-heading text-xl font-semibold text-[#A43032] mb-6">
                Avec l'OCR Raguia
              </h2>
              <ul className="space-y-4 text-white/90">
                {[
                  "Tous vos scans sont analysables par l'IA en secondes.",
                  "L'IA cite la page exacte d'un document numérisé.",
                  "Tableaux, formulaires, plans : tout extrait avec précision.",
                  "Traitement 100% en France, RGPD.",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-10 text-center">
            Cas d'usage
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: "Archives notariales",
                desc: "Décennies d'actes scannés rendus interrogeables instantanément.",
              },
              {
                title: "Diagnostics immobiliers",
                desc: "DPE, rapports amiante, diagnostics scannés : anomalies extraites en 2s.",
              },
              {
                title: "Factures & justificatifs",
                desc: "Factures papier scannées indexées et requêtables par vos équipes compta.",
              },
            ].map((uc) => (
              <div
                key={uc.title}
                className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-md hover:border-[#A43032]/30 hover:shadow-lg transition-all"
              >
                <div className="size-12 rounded-xl bg-[#A43032]/10 flex items-center justify-center mb-4">
                  <Zap className="size-6 text-[#A43032]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#A43032] mb-3">
                  {uc.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {uc.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-semibold text-[#010101] mb-8 text-center">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border-2 border-stone-200 overflow-hidden hover:border-[#A43032]/30 transition-all shadow-sm"
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-[#010101] hover:bg-stone-50 transition-colors"
                >
                  <span>{item.q}</span>
                  {open === i ? (
                    <ChevronUp className="size-5 text-[#A43032] shrink-0" />
                  ) : (
                    <ChevronDown className="size-5 text-stone-400 shrink-0" />
                  )}
                </button>
                {open === i && (
                  <div className="px-5 pb-5 border-t border-stone-100">
                    <p className="pt-4 text-stone-600 font-medium leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#A43032] to-[#8a2729] rounded-3xl p-10 text-white text-center shadow-2xl">
            <h2 className="font-heading text-3xl font-semibold mb-4">
              Vos archives enfin exploitables
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Testez Raguia sur vos propres PDF scannés. Mise en place en 24h,
              sans infrastructure.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#A43032] transition hover:scale-105"
            >
              Demander une démo <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
